import React, { useCallback, useEffect, useRef, useState } from 'react'

import * as S from './styles'

type VideoContainerProps = {
  src: string
}

export const VideoContainer: React.FC<VideoContainerProps> = ({ src }) => {
  const [isIntersecting, setIsIntersecting] = useState(false)

  const videoRef = useRef<HTMLVideoElement>(null)
  const videoControlsRef = useRef({
    isFirstClick: true,
    alreadyAutoPlayed: false,
  })

  const [isPaused, setIsPaused] = useState(false)

  useEffect(() => {
    if (!videoRef.current) return

    const observer = new IntersectionObserver(([entry]) => {
      setIsIntersecting(entry.isIntersecting)
    })

    observer.observe(videoRef.current)
    // eslint-disable-next-line consistent-return
    return () => observer.disconnect()
  }, [])

  const handleClickVideo = useCallback(
    (event?: Event) => {
      event?.preventDefault()

      const video = videoRef.current
      const videoControls = videoControlsRef.current

      if (!video) return

      if (videoControls.isFirstClick) {
        videoControls.isFirstClick = false
        video.muted = false
        return
      }

      if (video.paused) {
        video.play()
        return
      }

      video.pause()
    },
    [videoRef],
  )

  useEffect(() => {
    const video = videoRef.current

    if (!video) return

    const handleSetupVideo = () => {
      video.src = src
      video.muted = true
      video.preload = 'auto'
      video.playsInline = true
      video.autoplay = false
      video.controls = true
    }

    const handlePlayVideo = () => {
      const videoControls = videoControlsRef.current

      if (!videoControls.alreadyAutoPlayed) {
        video.play()
        videoControls.alreadyAutoPlayed = true
      }
    }

    handleSetupVideo()

    if (isIntersecting) handlePlayVideo()

    video.addEventListener('click', handleClickVideo)
    video.addEventListener('play', () => setIsPaused(false))
    video.addEventListener('pause', () => setIsPaused(true))
    window.addEventListener('blur', () => video.pause())

    // eslint-disable-next-line consistent-return
    return () => {
      video.removeEventListener('click', handleClickVideo)
      video.removeEventListener('play', () => setIsPaused(false))
      video.removeEventListener('pause', () => setIsPaused(true))
      window.removeEventListener('blur', () => video.pause())
    }
  }, [isIntersecting, src, handleClickVideo])

  return (
    <S.FlexContainerStyles>
      {isPaused && (
        <S.PlayButton onClick={() => handleClickVideo()}>
          <div className="play-icon" />
        </S.PlayButton>
      )}
      <S.Video ref={videoRef} controlsList="nodownload" />
    </S.FlexContainerStyles>
  )
}
